import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { OfflineBanner } from '../../components/lesson/OfflineBanner.js';
import { ApiError } from '../../lib/api.js';
import { fetchConflicts, resolveConflict, type LessonConflict } from '../../lib/lesson-api.js';
import { useOutbox } from '../../lib/offline/useOutbox.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * Conflict reconciliation (spec §10).
 *
 * A row here means two people edited the same field while at least one of them
 * was offline, and the server refused to choose. Spec §10 is explicit that one
 * submission must never silently overwrite another, so the server merges what it
 * can, records what it cannot, and leaves it here for a person.
 *
 * The screen shows BOTH values side by side and offers three exits — keep the
 * server's, take the device's, or write something that is neither. The third is
 * there because it is often the right answer: two people describing the same
 * event differently are usually each half right, and forcing a choice between
 * two incomplete accounts loses the half that was not picked.
 *
 * Nothing is pre-selected. A default would get accepted by reflex, and the whole
 * point of this screen is that somebody actually looked.
 */

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description of the issue',
  rootCause: 'Root cause',
  correction: 'Correction taken',
  correctiveAction: 'Corrective action',
  repeatLocation: 'Repeat location',
};

function fieldLabel(name: string): string {
  return FIELD_LABELS[name] ?? name;
}

export function LessonConflicts() {
  const outbox = useOutbox();
  const queryClient = useQueryClient();
  const [manual, setManual] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const conflicts = useQuery({ queryKey: ['lesson-conflicts'], queryFn: fetchConflicts });

  const resolve = useMutation({
    mutationFn: ({
      id,
      choice,
      value,
    }: {
      id: string;
      choice: 'server' | 'incoming' | 'manual';
      value?: string;
    }) => resolveConflict(id, { choice, ...(value === undefined ? {} : { value }) }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['lesson-conflicts'] });
      void queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : t("That could not be resolved.")),
  });

  const open: LessonConflict[] = (conflicts.data?.conflicts ?? []).filter(
    (conflict) => conflict.resolvedAt === null,
  );

  return (
    <div className="min-h-full">
      <AppTopBar app="lesson-learned" />
      <OfflineBanner state={outbox} />
      <Breadcrumbs app="lesson-learned" trail={['Reconcile changes']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Changes that need reconciling")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Two people edited the same field, and both versions were kept. Neither has been applied — choose which one this record should hold.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        {conflicts.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!conflicts.isLoading && open.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("Nothing to reconcile. Everything that synced merged cleanly.")}
          </p>
        )}

        <ul className="mt-6 grid list-none gap-5 p-0">
          {open.map((conflict) => (
            <li key={conflict.id} className="rounded-md border border-border bg-surface p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-base font-semibold">{fieldLabel(conflict.fieldName)}</h2>
                <Link
                  to={`/lesson-learned/lessons/${conflict.formId}`}
                  className="text-sm no-underline"
                >
                  {t("Open the lesson")}
                </Link>
              </div>

              {conflict.incomingClientAt && (
                <p className="mt-1 text-xs text-content-secondary">
                  {t("The incoming version was written on a device at")}{' '}
                  {formatDateTime(conflict.incomingClientAt)}.
                </p>
              )}

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <article className="rounded-sm border border-border p-3">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                    {t("On the server now")}
                  </h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm">
                    {conflict.serverValue?.trim() || <em className="text-content-secondary">{t("Empty")}</em>}
                  </p>
                  <button
                    type="button"
                    onClick={() => resolve.mutate({ id: conflict.id, choice: 'server' })}
                    disabled={resolve.isPending}
                    className="mt-3 rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                  >
                    {t("Keep this one")}
                  </button>
                </article>

                <article className="rounded-sm border border-border p-3">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-content-secondary">
                    {t("Came in from a device")}
                  </h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm">
                    {conflict.incomingValue?.trim() || (
                      <em className="text-content-secondary">{t("Empty")}</em>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={() => resolve.mutate({ id: conflict.id, choice: 'incoming' })}
                    disabled={resolve.isPending}
                    className="mt-3 rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                  >
                    {t("Use this one")}
                  </button>
                </article>
              </div>

              <div className="mt-4 rounded-sm border border-border p-3">
                <label htmlFor={`manual-${conflict.id}`} className="text-sm font-medium">
                  {t("Or write the version this record should hold")}
                </label>
                <p className="mt-1 text-xs text-content-secondary">
                  {t("Often the right answer when both accounts are partly right.")}
                </p>
                <textarea
                  id={`manual-${conflict.id}`}
                  value={manual[conflict.id] ?? ''}
                  onChange={(event) =>
                    setManual((current) => ({ ...current, [conflict.id]: event.target.value }))
                  }
                  rows={3}
                  className="mt-2 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() =>
                    resolve.mutate({
                      id: conflict.id,
                      choice: 'manual',
                      value: manual[conflict.id] ?? '',
                    })
                  }
                  disabled={!(manual[conflict.id] ?? '').trim() || resolve.isPending}
                  className="mt-2 rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {t("Save my version")}
                </button>
              </div>

              <p className="mt-3 text-xs text-content-secondary">
                {t("Whichever you choose, both versions stay in the audit log — including the one you did not keep.")}
              </p>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
